import { QuestionIcon } from '@phosphor-icons/react'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import classNames from 'classnames'
import React, { useState } from 'react'
import z from 'zod'
import * as styles from './tooltip.css.ts'

export const TooltipSideSchema = z.enum(['top', 'right', 'bottom', 'left'])
export type TooltipSide = z.infer<typeof TooltipSideSchema>
export const TooltipWithTriggerSizeSchema = z.enum(['small', 'normal'])
export type TooltipWithTriggerSize = z.infer<typeof TooltipWithTriggerSizeSchema>

type TooltipProps = {
  children: React.ReactNode
  iconClassName?: string
  contentClassName?: string
  side?: TooltipSide
  sideOffset?: number
  width?: number
  label?: string
  initialOpen?: boolean
}

// Necessary to enable tooltip on click on mobile since Radix designers won't add this functionality
// https://github.com/radix-ui/primitives/issues/955
function TooltipWithClickToOpen({
  children,
  iconClassName,
  contentClassName,
  side = 'top',
  sideOffset = 3,
  width,
  label,
  initialOpen,
}: TooltipProps) {
  const [open, setOpen] = useState(initialOpen ?? false)

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root open={open} delayDuration={0} onOpenChange={setOpen}>
        <RadixTooltip.Trigger asChild>
          <button
            onClick={() => setOpen(true)}
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            aria-label={label}
          >
            <QuestionIcon className={classNames(styles.infoIcon, iconClassName)} size={16} weight="regular" />
          </button>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            className={classNames(styles.tooltip, contentClassName)}
            sideOffset={sideOffset}
            side={side}
          >
            <div className={styles.tooltipContent} style={width ? { width: `${width}px` } : { maxWidth: '300px' }}>
              {children}
            </div>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}

type TooltipWithTriggerProps = React.PropsWithChildren<
  Pick<RadixTooltip.TooltipProps, 'open'> &
    Pick<RadixTooltip.TooltipContentProps, 'sideOffset' | 'side' | 'align' | 'alignOffset'> & {
      trigger: React.ReactElement
      maxWidth?: number
      size?: TooltipWithTriggerSize
      /** When true, renders tooltip content without default padding and background styling. */
      bare?: boolean
    }
>

export const TooltipWithHover = ({
  trigger,
  children,
  maxWidth = 300,
  side = 'top',
  size = 'normal',
  sideOffset = 5,
  align,
  alignOffset = 0,
  open,
  bare = false,
}: TooltipWithTriggerProps) => {
  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root delayDuration={0} open={open}>
        <RadixTooltip.Trigger asChild>{trigger}</RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            className={bare ? undefined : classNames(styles.tooltip, { [styles.smallTooltip]: size === 'small' })}
            style={{ maxWidth: maxWidth }}
            sideOffset={sideOffset}
            side={side}
            align={align}
            alignOffset={alignOffset}
          >
            {bare ? children : <div className={styles.tooltipContent}>{children}</div>}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  )
}

export { TooltipWithClickToOpen as Tooltip }
